/* ============================================================================
   FANLETIC — native predictive search (assets/fanletic-predictive-search.js)
   ----------------------------------------------------------------------------
   Vanilla JS typeahead for the District theme's header search inputs.
   Replaces the Boost instant-search popup with Shopify's native
   /search/suggest.json predictive search API.

   Attach points (verified in theme source):
     - snippets/header-util.liquid  -> header.util .search-wrapper
         form.input-group.search-bar > .input-wrapper > input[type=search][name=q]
     - sections/header.liquid       -> #menu.panel .search
         form.input-group.search-bar > .input-wrapper > input[type=search][name=q]
   Both render snippets/search-bar.liquid, so the shared hook is
   `form.search-bar input[name="q"]` scoped to header.util / #menu.panel
   (with an unscoped fallback in case the header markup shifts).
   The Fanletic search results page uses its own .fanletic-search__form class,
   so this script deliberately does NOT attach there.

   Behavior:
     - debounced 200ms, min 2 chars
     - GET /search/suggest.json?q=<term>
         &resources[type]=product,collection,query&resources[limit]=6
     - navy dropdown panel: query suggestions, product rows (thumb / Barlow
       title / price), collections, "see all results" footer
     - keyboard: ArrowUp/ArrowDown/Enter/Escape; ARIA 1.2 combobox pattern
     - zero dependencies; styles injected via a <style> tag below
   ========================================================================== */
(function () {
  'use strict';

  var DEBOUNCE_MS = 200;
  var MIN_CHARS = 2;
  var LIMIT = 6;
  var NS = 'fl-ps'; // class / id namespace
  var instanceCount = 0;

  /* ---------------------------------------------------------------- styles */
  var CSS = [
    '.' + NS + '-anchor { position: relative; }',
    '.' + NS + '-panel {',
    '  position: absolute;',
    '  top: 100%;',
    '  left: 0;',
    '  right: 0;',
    '  z-index: 9999;',
    '  min-width: 280px;',
    '  margin-top: 4px;',
    '  background: #12123C;',
    '  border: 1px solid rgba(139, 147, 232, .35);',
    '  border-radius: 0;',
    '  box-shadow: 0 10px 28px rgba(18, 18, 60, .35);',
    '  max-height: min(70vh, 560px);',
    '  overflow-y: auto;',
    '  text-align: left;',
    '}',
    '.' + NS + '-panel[hidden] { display: none; }',
    '.' + NS + '-list { list-style: none; margin: 0; padding: 6px 0; }',
    '.' + NS + '-group-label {',
    "  font-family: 'Barlow Semi Condensed', 'Arial Narrow', Arial, sans-serif;",
    '  font-weight: 600;',
    '  font-size: 11px;',
    '  letter-spacing: .1em;',
    '  text-transform: uppercase;',
    '  color: #8B93E8;',
    '  padding: 10px 14px 4px;',
    '}',
    '.' + NS + '-option {',
    '  display: flex;',
    '  align-items: center;',
    '  gap: 10px;',
    '  padding: 8px 14px;',
    '  cursor: pointer;',
    '  color: #FFFFFF;',
    '  text-decoration: none;',
    '}',
    '.' + NS + '-option[aria-selected="true"], .' + NS + '-option:hover {',
    '  background: rgba(139, 147, 232, .18);',
    '}',
    '.' + NS + '-thumb {',
    '  flex: 0 0 44px;',
    '  width: 44px;',
    '  height: 44px;',
    '  object-fit: contain;',
    '  background: #FFFFFF;',
    '  border: 1px solid rgba(139, 147, 232, .35);',
    '  border-radius: 0;',
    '}',
    '.' + NS + '-thumb--empty { background: rgba(255, 255, 255, .08); border-style: dashed; }',
    '.' + NS + '-text { min-width: 0; flex: 1 1 auto; }',
    '.' + NS + '-title {',
    '  display: block;',
    "  font-family: 'Barlow Semi Condensed', 'Arial Narrow', Arial, sans-serif;",
    '  font-weight: 600;',
    '  font-size: 14px;',
    '  letter-spacing: .04em;',
    '  text-transform: uppercase;',
    '  color: #FFFFFF;',
    '  line-height: 1.2;',
    '  white-space: nowrap;',
    '  overflow: hidden;',
    '  text-overflow: ellipsis;',
    '}',
    '.' + NS + '-price {',
    '  display: block;',
    "  font-family: 'Barlow Semi Condensed', 'Arial Narrow', Arial, sans-serif;",
    '  font-weight: 700;',
    '  font-size: 13px;',
    '  letter-spacing: .04em;',
    '  color: #8B93E8;',
    '  margin-top: 2px;',
    '}',
    '.' + NS + '-query-term { color: #8B93E8; }',
    '.' + NS + '-footer {',
    '  display: block;',
    '  padding: 11px 14px;',
    '  border-top: 1px solid rgba(139, 147, 232, .35);',
    "  font-family: 'Barlow Semi Condensed', 'Arial Narrow', Arial, sans-serif;",
    '  font-weight: 700;',
    '  font-size: 13px;',
    '  letter-spacing: .08em;',
    '  text-transform: uppercase;',
    '  color: #FFFFFF;',
    '  text-decoration: none;',
    '  cursor: pointer;',
    '}',
    '.' + NS + '-footer[aria-selected="true"], .' + NS + '-footer:hover {',
    '  background: rgba(139, 147, 232, .18);',
    '}',
    '.' + NS + '-empty {',
    '  padding: 14px;',
    "  font-family: 'Assistant', 'Helvetica Neue', Helvetica, Arial, sans-serif;",
    '  font-size: 14px;',
    '  color: rgba(255, 255, 255, .75);',
    '}',
    '@media screen and (max-width: 749px) {',
    '  .' + NS + '-panel { max-height: 60vh; }',
    '}'
  ].join('\n');

  function injectStyles() {
    if (document.getElementById(NS + '-styles')) return;
    var style = document.createElement('style');
    style.id = NS + '-styles';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  /* --------------------------------------------------------------- helpers */
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function thumbUrl(url) {
    if (!url) return null;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=88';
  }

  // suggest.json returns bare amount strings like "24.99".
  // Use the theme's money format when available (District exposes
  // StyleHatch.currencyFormat, e.g. "${{amount}}"), else fall back to $.
  function formatMoney(amount) {
    if (amount === null || amount === undefined || amount === '') return '';
    var fmt = (window.StyleHatch && window.StyleHatch.currencyFormat) || '${{amount}}';
    fmt = fmt.replace(/<[^>]*>/g, ''); // strip any HTML in the format string
    var out = fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/i, String(amount));
    return out.indexOf(String(amount)) > -1 ? out : '$' + amount;
  }

  function productPrice(p) {
    var price = formatMoney(p.price);
    if (p.price_min !== undefined && p.price_max !== undefined && p.price_min !== p.price_max) {
      return 'From ' + formatMoney(p.price_min);
    }
    return price;
  }

  /* ------------------------------------------------------------- typeahead */
  function attach(input) {
    if (!input || input.dataset.flPs === '1') return;
    input.dataset.flPs = '1';

    var form = input.closest('form') || input.parentElement;
    form.classList.add(NS + '-anchor');

    var idx = ++instanceCount;
    var panelId = NS + '-panel-' + idx;
    var listId = NS + '-list-' + idx;

    var panel = document.createElement('div');
    panel.className = NS + '-panel';
    panel.id = panelId;
    panel.hidden = true;
    form.appendChild(panel);

    // ARIA combobox wiring
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listId);
    input.setAttribute('autocomplete', 'off');

    var options = [];      // flat, DOM order — for keyboard navigation
    var activeIndex = -1;
    var controller = null; // in-flight fetch
    var cache = {};        // term -> resources
    var cacheKeys = [];
    var lastTerm = '';

    function close() {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      options = [];
      activeIndex = -1;
    }

    function setActive(i) {
      if (activeIndex > -1 && options[activeIndex]) {
        options[activeIndex].setAttribute('aria-selected', 'false');
      }
      activeIndex = i;
      if (i > -1 && options[i]) {
        options[i].setAttribute('aria-selected', 'true');
        input.setAttribute('aria-activedescendant', options[i].id);
        if (options[i].scrollIntoView) {
          options[i].scrollIntoView({ block: 'nearest' });
        }
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    function addOption(el, url) {
      el.id = NS + '-opt-' + idx + '-' + options.length;
      el.setAttribute('role', 'option');
      el.setAttribute('aria-selected', 'false');
      el.dataset.url = url;
      // mousedown, not click: fires before the input blurs / theme closes bar
      el.addEventListener('mousedown', function (e) {
        e.preventDefault();
        window.location.href = url;
      });
      options.push(el);
      return el;
    }

    function groupLabel(text) {
      var li = document.createElement('li');
      li.className = NS + '-group-label';
      li.setAttribute('role', 'presentation');
      li.textContent = text;
      return li;
    }

    function render(term, resources) {
      var products = (resources.products || []);
      var collections = (resources.collections || []);
      var queries = (resources.queries || []);

      panel.textContent = '';
      options = [];
      activeIndex = -1;

      var list = document.createElement('ul');
      list.className = NS + '-list';
      list.id = listId;
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', 'Search suggestions');

      // 1. query suggestions
      if (queries.length) {
        list.appendChild(groupLabel('Suggestions'));
        queries.forEach(function (q) {
          var li = addOption(document.createElement('li'), q.url || ('/search?q=' + encodeURIComponent(q.text)));
          li.className = NS + '-option';
          var span = document.createElement('span');
          span.className = NS + '-title';
          span.textContent = q.text;
          li.appendChild(span);
          list.appendChild(li);
        });
      }

      // 2. products
      if (products.length) {
        list.appendChild(groupLabel('Products'));
        products.forEach(function (p) {
          var li = addOption(document.createElement('li'), p.url);
          li.className = NS + '-option';

          var imgUrl = p.featured_image && p.featured_image.url ? thumbUrl(p.featured_image.url) : (p.image ? thumbUrl(p.image) : null);
          if (imgUrl) {
            var img = document.createElement('img');
            img.className = NS + '-thumb';
            img.src = imgUrl;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 44;
            img.height = 44;
            li.appendChild(img);
          } else {
            var ph = document.createElement('span');
            ph.className = NS + '-thumb ' + NS + '-thumb--empty';
            li.appendChild(ph);
          }

          var text = document.createElement('span');
          text.className = NS + '-text';
          var title = document.createElement('span');
          title.className = NS + '-title';
          title.textContent = p.title;
          text.appendChild(title);
          var price = document.createElement('span');
          price.className = NS + '-price';
          price.textContent = productPrice(p);
          text.appendChild(price);
          li.appendChild(text);
          list.appendChild(li);
        });
      }

      // 3. collections
      if (collections.length) {
        list.appendChild(groupLabel('Collections'));
        collections.forEach(function (c) {
          var li = addOption(document.createElement('li'), c.url);
          li.className = NS + '-option';
          var span = document.createElement('span');
          span.className = NS + '-title';
          span.textContent = c.title;
          li.appendChild(span);
          list.appendChild(li);
        });
      }

      var any = options.length > 0;

      if (!any) {
        var empty = document.createElement('div');
        empty.className = NS + '-empty';
        empty.textContent = 'No matches for “' + term + '” — check spelling or try a team name.';
        panel.appendChild(empty);
      } else {
        panel.appendChild(list);
      }

      // 4. footer: full results (products + pages — no type param)
      var footer = addOption(document.createElement('a'), '/search?q=' + encodeURIComponent(term));
      footer.className = NS + '-footer';
      footer.href = '/search?q=' + encodeURIComponent(term);
      footer.textContent = 'See all results for “' + term + '”';
      panel.appendChild(footer);

      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function fetchSuggestions(term) {
      if (cache[term]) {
        render(term, cache[term]);
        return;
      }
      if (controller) controller.abort();
      controller = ('AbortController' in window) ? new AbortController() : null;

      var url = '/search/suggest.json' +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product,collection,query' +
        '&resources[limit]=' + LIMIT +
        '&resources[options][unavailable_products]=last';

      fetch(url, {
        signal: controller ? controller.signal : undefined,
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)); })
        .then(function (data) {
          var resources = (data.resources && data.resources.results) || {};
          cache[term] = resources;
          cacheKeys.push(term);
          if (cacheKeys.length > 40) delete cache[cacheKeys.shift()];
          // stale-response guard: only render if the term is still current
          if (input.value.trim() === term) render(term, resources);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          close(); // network hiccup: fail silent, form submit still works
        });
    }

    var onInput = debounce(function () {
      var term = input.value.trim();
      if (term === lastTerm) return;
      lastTerm = term;
      if (term.length < MIN_CHARS) {
        close();
        return;
      }
      fetchSuggestions(term);
    }, DEBOUNCE_MS);

    input.addEventListener('input', onInput);

    input.addEventListener('keydown', function (e) {
      if (panel.hidden) {
        // reopen previously fetched results on ArrowDown
        if (e.key === 'ArrowDown' && cache[input.value.trim()]) {
          e.preventDefault();
          render(input.value.trim(), cache[input.value.trim()]);
        }
        return;
      }
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActive(activeIndex >= options.length - 1 ? 0 : activeIndex + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActive(activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
          break;
        case 'Enter':
          if (activeIndex > -1 && options[activeIndex]) {
            e.preventDefault();
            window.location.href = options[activeIndex].dataset.url;
          }
          // no active option: let the form submit to /search?q=… naturally
          break;
        case 'Escape':
          e.preventDefault();
          close();
          input.focus();
          break;
        case 'Tab':
          close();
          break;
      }
    });

    // Close when clicking/tapping outside the form
    document.addEventListener('pointerdown', function (e) {
      if (!panel.hidden && !form.contains(e.target)) close();
    });

    // Keep the dropdown from re-opening stale content on focus,
    // but do reopen current results (common "click back into field" case)
    input.addEventListener('focus', function () {
      var term = input.value.trim();
      if (term.length >= MIN_CHARS && cache[term] && panel.hidden) {
        render(term, cache[term]);
      }
    });
  }

  /* ------------------------------------------------------------------ init */
  function init() {
    injectStyles();
    // Header util bar + drawer menu searches (both include snippets/search-bar.liquid)
    var inputs = document.querySelectorAll(
      'header.util form.search-bar input[name="q"], #menu.panel form.search-bar input[name="q"]'
    );
    if (!inputs.length) {
      // fallback if header markup shifts — attach to any theme search bar
      // except the results page's own form (.fanletic-search__form)
      inputs = document.querySelectorAll('form.search-bar input[name="q"]');
    }
    Array.prototype.forEach.call(inputs, attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init(); // script is loaded async — DOM may already be ready
  }
})();
